import { PageHeader } from "@/components/PageHeader";
import { useConfirm } from "@/contexts/ConfirmDialogContext";
import { api } from "@/lib/api";
import { formatDate } from "@/lib/utils";
import { Button, Text } from "@a7y/tailwind-catalyst";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ShieldAlert } from "lucide-react";
import { useState } from "react";
import { LoadingPanel, Section, SignalEvidence } from "./shared";
import type { SuspiciousReferralRow, SuspiciousVerdict } from "./types";

type StatusMessage = { type: "success" | "error"; text: string };

export function SuspiciousQueuePage() {
  const queryClient = useQueryClient();
  const confirm = useConfirm();
  const [statusMessage, setStatusMessage] = useState<StatusMessage | null>(null);

  const showStatus = (type: "success" | "error", text: string) => {
    setStatusMessage({ type, text });
    setTimeout(() => setStatusMessage(null), 4000);
  };

  const { data, isLoading } = useQuery({
    queryKey: ["affiliate-suspicious-queue"],
    queryFn: () => api.affiliates.listSuspiciousQueue(),
  });

  const rows: SuspiciousReferralRow[] = data?.referrals ?? [];

  const resolveMutation = useMutation({
    mutationFn: (args: { affiliateId: string; referralId: string; verdict: SuspiciousVerdict }) =>
      api.affiliates.resolveSuspicious(args.affiliateId, {
        referralId: args.referralId,
        verdict: args.verdict,
      }),
    onSuccess: (_res, args) => {
      queryClient.invalidateQueries({ queryKey: ["affiliate-suspicious-queue"] });
      queryClient.invalidateQueries({ queryKey: ["affiliates"] });
      showStatus(
        "success",
        args.verdict === "fraud"
          ? "Confirmed fraud — affiliate disabled"
          : "Marked legitimate",
      );
    },
    onError: (err: Error) => showStatus("error", `Resolve failed: ${err.message}`),
  });

  const handleResolve = async (row: SuspiciousReferralRow, verdict: SuspiciousVerdict) => {
    const fraud = verdict === "fraud";
    const ok = await confirm({
      title: fraud
        ? `Confirm fraud for ${row.affiliateEmail}?`
        : `Mark referral legitimate?`,
      description: fraud
        ? "Confirming fraud clears the suspicious flag and DISABLES the affiliate. A Slack alert is posted to ops."
        : "Clears the suspicious flag on this referral so its commission can proceed.",
      confirmLabel: fraud ? "Confirm fraud" : "Mark legitimate",
      confirmColor: fraud ? "red" : "green",
    });
    if (ok) {
      resolveMutation.mutate({
        affiliateId: row.referral.affiliateId,
        referralId: row.referral.id,
        verdict,
      });
    }
  };

  return (
    <div className="px-4 py-0">
      <PageHeader
        title="Suspicious Referral Queue"
        subtitle="Self-referral fraud signals awaiting review"
      />

      {statusMessage && (
        <div
          className={`mb-4 rounded-lg px-4 py-2.5 text-sm font-medium ${
            statusMessage.type === "success"
              ? "bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-400"
              : "bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-400"
          }`}
        >
          {statusMessage.text}
        </div>
      )}

      {isLoading ? (
        <LoadingPanel />
      ) : rows.length === 0 ? (
        <div className="py-16 text-center">
          <ShieldAlert className="mx-auto h-12 w-12 text-zinc-400" />
          <Text className="mt-2 text-zinc-500">No suspicious referrals to review</Text>
        </div>
      ) : (
        <div className="space-y-4">
          {rows.map((row) => (
            <Section
              key={row.referral.id}
              title={row.affiliateEmail}
              actions={
                <div className="flex items-center gap-2">
                  <Button
                    color="green"
                    disabled={resolveMutation.isPending}
                    onClick={() => handleResolve(row, "legitimate")}
                  >
                    Legitimate
                  </Button>
                  <Button
                    color="red"
                    disabled={resolveMutation.isPending}
                    onClick={() => handleResolve(row, "fraud")}
                  >
                    Confirm fraud
                  </Button>
                </div>
              }
            >
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="space-y-1 text-sm">
                  <Row label="Affiliate email" value={<span className="font-mono">{row.affiliateEmail}</span>} />
                  <Row label="Referral org" value={<span className="font-mono">{row.referral.orgId || "—"}</span>} />
                  <Row label="Attribution" value={row.referral.attributionSource || "—"} />
                  <Row
                    label="Converted"
                    value={row.referral.convertedAt ? formatDate(row.referral.convertedAt) : "—"}
                  />
                </div>
                <div>
                  <div className="mb-1 text-xs font-medium uppercase tracking-wide text-zinc-500 dark:text-zinc-400">
                    Evidence
                  </div>
                  <SignalEvidence signals={row.signals} />
                </div>
              </div>
            </Section>
          ))}
        </div>
      )}
    </div>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex gap-2">
      <span className="w-32 shrink-0 text-xs font-medium uppercase tracking-wide text-zinc-500 dark:text-zinc-400">
        {label}
      </span>
      <span className="text-zinc-900 dark:text-zinc-100">{value}</span>
    </div>
  );
}
